import Head from 'next/head';
import Icons from 'components/icons';
import ImageViewerWrapper from 'compositions/ImageViewerWrapper';
import Loading from 'components/Loading';
import Position from 'components/ui/Position';
import RecipeDrawer from 'compositions/RecipeDrawer';
import RecipeValueVisualization from 'compositions/RecipeValueVisualization';
import Sidebars from 'compositions/Sidebars';
import useScreenSizeGuard from 'hooks/useScreenSizeGuard';
import { sanityFetch } from 'api';
import {
  General,
  KnowledgeHubItem,
  NextArray,
  Onboarding,
  Room,
  RoomTypeLink,
} from 'type';
import { ImageUtil, RoomUtil } from 'utils';
import { selectGeneralState, setGeneral } from 'store/generalSlice';
import { selectRoomState, setLightingSolution, setRoom } from 'store/roomSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useEffect, useState } from 'react';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { roomFetchQuery } from 'utils/roomFetchQuery';
import { useTranslation } from 'hooks/useTranslation';
import enabledLanguages from '../../../enabledLanguages';
import { knowledgeHubItemsFetchQuery } from 'utils/knowledgeHubItemsFetchQuery';
import OnboardingViewer from 'compositions/OnboardingViewer';
// import { AttentionTips } from 'compositions/AttentionTip';
import { useSearchParams } from 'next/navigation';
import { selectUiState, setUi } from 'store/uiSlice';
// import AttentionHotspots from 'compositions/AttentionHotspots';
import { AttentionGrabbers } from 'compositions/AttentionGrabbers';
import generalData from 'translations/general.json';

export type ThreeDRoomPageProps = {
  locale: string;
  room: Room;
  general: General;
  onboarding: Onboarding;
};

const TEXT_CHANGE_DELAY = 1000;
const PRELOADING_DELAY = 2000;

import onboardingData from 'translations/onboarding.json';
import ContactFormModal from 'compositions/ContactFormModal';

const ThreeDRoomPage: NextPage<ThreeDRoomPageProps> = ({
  room,
  locale,
  // general,
  // onboarding,
}) => {
  useScreenSizeGuard();
  const translation = useTranslation();
  const [isRecipeVisualizationOpen, setIsRecipeVisualizationOpen] =
    useState(false);
  const [preloading, setPreloading] = useState(true);
  const [loadingText, setLoadingText] = useState(
    translation('room.loading.creatingRoom'),
  );
  const [isRecipeOpen, setIsRecipeOpen] = useState(false);
  const searchParams = useSearchParams();
  const contact = searchParams.get('contact');

  const currentRoom = useAppSelector(selectRoomState);
  const currentGeneral = useAppSelector(selectGeneralState);
  const ui = useAppSelector(selectUiState) || {};

  const dispatch = useAppDispatch();
  const lang = (locale || 'en') as any;

  const localeOnboardingData = onboardingData as any;

  const onboarding = {
    ...localeOnboardingData[lang],
  } as Onboarding;

  const localeGeneralData = generalData as any;

  const general = {
    ...localeGeneralData[lang],
  } as General;

  const handleOpenRecipe = () => {
    setLoadingText(translation('room.loading.creatingRecipe'));
    setPreloading(true);

    dispatch(
      setUi({
        ...ui,
        showOnboarding: false,
      }),
    );

    setTimeout(() => {
      setIsRecipeOpen(true);

      setTimeout(() => {
        setPreloading(false);
      }, PRELOADING_DELAY);
    }, TEXT_CHANGE_DELAY);
  };

  useEffect(() => {
    if (contact) {
      dispatch(
        setUi({
          ...ui,
          showContactForm: true,
        }),
      );
    }
  }, [contact]);

  useEffect(() => {
    const preloadImages = async () => {
      setPreloading(true);

      const allRoomImages = RoomUtil.getAllRoomImages(currentRoom);
      await ImageUtil.preloadAll(allRoomImages);

      setTimeout(() => {
        setPreloading(false);

        setTimeout(() => {
          setLoadingText(translation('room.loading.loading'));
          setIsRecipeVisualizationOpen(true);
        }, TEXT_CHANGE_DELAY);
      }, PRELOADING_DELAY);
    };

    if (currentRoom) {
      preloadImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoom]);

  useEffect(() => {
    if (!currentRoom) {
      dispatch(setRoom(room));

      const [lightingSolution] = room.lightingSolutions;
      dispatch(setLightingSolution(lightingSolution));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  useEffect(() => {
    if (!currentGeneral) {
      dispatch(setGeneral(general));
    }
  }, [general, dispatch, currentGeneral]);

  return (
    <>
      <Head>
        <title>Recipe of Light - {room.title}</title>
      </Head>
      <ImageViewerWrapper
        loadingText={loadingText}
        overlayText={general.dragOverlay?.text ?? ''}
      />
      <Position position="topRight">
        <Icons.Logo className="h-4 w-[128px] text-white" />
      </Position>
      <RecipeValueVisualization
        isOpen={isRecipeVisualizationOpen}
        onClose={() => setIsRecipeVisualizationOpen(false)}
        onOpen={() => setIsRecipeVisualizationOpen(true)}
        onOpenRecipeClick={handleOpenRecipe}
      />
      <Sidebars />
      <div className="absolute bottom-2 lg:bottom-4 right-4 text-xs text-grey-900">
        {general?.miscellaneous?.copyrightText}
      </div>
      <RecipeDrawer
        isOpen={isRecipeOpen}
        onClose={() => setIsRecipeOpen(false)}
      />

      {!preloading && (
        <>
          {/* <AttentionTips
            tips={[
              {
                id: 'show-recipe-button',
                text: 'Dig deeper into the recipe',
                delay: 10000,
              },
              {
                id: 'lighting-solutions',
                text: 'Check out our lighting solutions',
                delay: 2000,
              },
              {
                id: 'lighting-controls',
                text: 'Check out our lighting controls',
                delay: 4000,
              },
            ]}
            delay={10000}
          /> */}

          <AttentionGrabbers
            tips={[
              {
                id: 'show-recipe-button',
                text: translation('attention.recipe'),
                dependsOn: { canOpenRecipe: true, rightPanelOpen: true },
                delay: 150000,
              },
              // {
              //   id: 'lighting-solutions',
              //   text: 'Check out our lighting solutions',
              //   delay: 15000,
              // },
              {
                id: 'lighting-controls',
                text: translation('attention.controls'),
                delay: 90000,
              },
            ]}
            dependsOn={{ showOnboarding: false }}
          />

          {/* <AttentionHotspots
            hotspots={[
              {
                id: 'show-recipe-button',
                text: 'Dig deeper into the recipe',
                delay: 10000,
              },
              {
                id: 'lighting-solutions',
                text: 'Check out our lighting solutions',
                delay: 2000,
              },
              {
                id: 'lighting-controls',
                text: 'Check out our lighting controls',
                delay: 4000,
              },
            ]}
          /> */}
        </>
      )}

      <Loading
        opaqueBackdrop
        show={preloading}
        text={loadingText}
        className="duration-1000 z-10"
      />
      <ContactFormModal />

      {onboarding && !preloading && (
        <OnboardingViewer onboarding={onboarding} />
      )}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const query =
    '*[_type == "roomConfig"]{_id,title,"slugName":slug.current,"lang":__i18n_lang}';
  const rooms = await sanityFetch<RoomTypeLink[]>(query);
  const paths = rooms
    .filter((path) => enabledLanguages.short.includes(path.lang))
    .filter(RoomUtil.isRoomTypeValid)
    .map((roomTypeLink) => ({
      params: {
        room: roomTypeLink.slugName,
      },
      locale: roomTypeLink.lang,
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<
  ThreeDRoomPageProps,
  { room: string }
> = async (context) => {
  const lang = context.locale || 'en';
  const roomName = context.params?.room || '';

  const query = `*[_type == "general" && __i18n_lang == "${lang}"]`;
  const [general] = await sanityFetch<General[]>(query);

  const onboardingQuery = `*[_type == "onboarding" && __i18n_lang == "${lang}"]{steps,buttons}`;
  const [onboarding] = await sanityFetch<Onboarding[]>(onboardingQuery);

  // console.log('onboarding', onboarding);

  const fetchQuery = roomFetchQuery(roomName, lang);
  const [_room] = await sanityFetch<Room[]>(fetchQuery);
  const knowledgeHubQuery = knowledgeHubItemsFetchQuery(_room.settingsId, lang);
  const knowledgeHubItems =
    await sanityFetch<NextArray<KnowledgeHubItem>>(knowledgeHubQuery);
  const room: Room = { ..._room, knowledgeHubItems };

  return {
    props: { room, general, locale: lang, onboarding: onboarding || null },
  };
};

export default ThreeDRoomPage;
